import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Search } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import { MAX_CHAT_SOURCES, SourceDoc, extLabel, selectAllState } from './workspace.types';
import { WorkspacePanel } from './WorkspacePanel';

interface SourcesPanelProps {
  documents: SourceDoc[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  activeId: string | null;
  onSelectActive: (id: string) => void;
  selectedIds: Set<string>;
  onToggleSelected: (id: string) => void;
  onToggleAll: () => void;
  canManage: boolean;
  /** Opens the workspace's file picker (the phone header's plus opens the same one). */
  onAddSource: () => void;
  uploading: boolean;
  onCollapse: () => void;
  /** Phones: the list alone, under the screen header. */
  bare?: boolean;
}

const SKELETON_ROWS = 4;

/** Checkboxes pick the RAG scope; clicking a row makes that source the one the Studio tools run against. */
export const SourcesPanel: React.FC<SourcesPanelProps> = ({
  documents,
  isLoading,
  isError,
  onRetry,
  activeId,
  onSelectActive,
  selectedIds,
  onToggleSelected,
  onToggleAll,
  canManage,
  onAddSource,
  uploading,
  onCollapse,
  bare = false,
}) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');

  // A box shows whether the source is in the chat's scope, which only a processed
  // source can be; one still processing keeps its pick and joins once it's ready.
  const readyDocs = documents.filter((d) => d.processingStatus === 'completed');
  const allState = selectAllState(documents, selectedIds);
  // The chat takes at most MAX_CHAT_SOURCES checked sources; say so once the limit can bite.
  const showLimitHint = documents.length > MAX_CHAT_SOURCES || selectedIds.size >= MAX_CHAT_SOURCES;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((d) => d.title.toLowerCase().includes(q));
  }, [documents, filter]);

  const renderList = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-0.5" aria-hidden>
          {Array.from({ length: SKELETON_ROWS }, (_, row) => (
            <div key={row} data-source-skeleton className="flex items-center gap-2.5 px-2.5 py-[9px]">
              <Skeleton className="h-[26px] w-[38px] rounded-md" />
              <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                <Skeleton className={cn('h-3.5', row % 2 ? 'w-3/5' : 'w-4/5')} />
                <Skeleton className="h-3 w-2/5" />
              </span>
              <Skeleton className="h-4 w-4 rounded-sm" />
            </div>
          ))}
        </div>
      );
    }
    if (isError) {
      return (
        <EmptyState
          className="px-3 py-10"
          title={t('failed_to_load')}
          secondaryAction={{ label: t('retry'), onClick: onRetry }}
        />
      );
    }
    if (documents.length === 0) {
      return <p className="px-3 py-10 text-center text-[13px] leading-5 text-muted-foreground">{t('aigen.no_sources')}</p>;
    }
    if (filtered.length === 0) {
      return (
        <EmptyState
          className="px-3 py-10"
          title={t('no_results_found')}
          secondaryAction={{ label: t('clear_search'), onClick: () => setFilter('') }}
        />
      );
    }
    return (
      <ul className="flex flex-col gap-0.5">
        {filtered.map((doc) => {
          const active = doc._id === activeId;
          const ready = doc.processingStatus === 'completed';
          const busy = doc.processingStatus === 'pending' || doc.processingStatus === 'processing';
          const failed = doc.processingStatus === 'failed';
          return (
            <li
              key={doc._id}
              className={cn(
                'relative flex items-center gap-2.5 rounded-lg px-2.5 py-[9px] transition-colors',
                active ? 'bg-muted' : 'hover:bg-muted/40',
              )}
            >
              {/* The row is one button (stretched over the row); the checkbox sits above it. */}
              <button
                type="button"
                onClick={() => onSelectActive(doc._id)}
                aria-current={active ? 'true' : undefined}
                className="flex min-w-0 flex-1 items-center gap-2.5 text-left after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring"
              >
                <span className="inline-flex h-[26px] w-[38px] shrink-0 items-center justify-center rounded-md border border-border bg-card font-mono text-[9.5px] font-semibold text-foreground/80">
                  {extLabel(doc.fileName)}
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-px leading-tight">
                  <span className={cn('truncate text-[13.5px] text-foreground', active ? 'font-semibold' : 'font-medium')}>
                    {doc.title}
                  </span>
                  <span
                    className={cn(
                      'truncate text-xs leading-tight',
                      busy ? 'text-warning-700' : failed ? 'text-error-700' : 'text-muted-foreground',
                    )}
                  >
                    {busy ? `${t('processing')}…` : failed ? t('processing_failed') : doc.fileName}
                  </span>
                </span>
              </button>
              <Checkbox
                checked={ready && selectedIds.has(doc._id)}
                onCheckedChange={() => onToggleSelected(doc._id)}
                disabled={!ready}
                aria-label={doc.title || doc.fileName}
                // The 16px box keeps a 40px hit area, so a thumb doesn't land on the row instead.
                className="relative z-10 shrink-0 before:absolute before:-inset-3 disabled:opacity-35"
              />
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <WorkspacePanel
      title={t('aigen.sources')}
      meta={isLoading || isError ? undefined : <span className="tabular-nums">{documents.length}</span>}
      onCollapse={onCollapse}
      side="left"
      bare={bare}
    >
      {/* Add + search */}
      <div className="flex shrink-0 flex-col gap-2.5 p-3">
        {canManage && (
          <Button variant="outline" className="h-11 w-full gap-1.5 md:h-9" onClick={onAddSource} disabled={uploading}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {t('aigen.add_sources')}
          </Button>
        )}
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('search')}
            aria-label={t('search')}
            className="h-10 pl-8 md:h-8 md:text-[13px]"
          />
        </div>
      </div>

      {/* Select all: checked, cleared, or the lime dash when only some are picked */}
      {!isLoading && !isError && documents.length > 0 && (
        <label className="flex shrink-0 cursor-pointer items-center justify-between border-y border-border py-2 pl-[22px] pr-5 text-[13px] text-muted-foreground transition-colors hover:text-foreground">
          <span>{t('select_all')}</span>
          <Checkbox checked={allState} onCheckedChange={onToggleAll} disabled={readyDocs.length === 0} />
        </label>
      )}

      {showLimitHint && !isLoading && !isError && (
        <p data-sources-limit-hint className="shrink-0 border-b border-border px-5 py-2 text-xs leading-4 text-muted-foreground">
          {t('aigen.max_sources', { count: MAX_CHAT_SOURCES })}
        </p>
      )}

      {/* Sources */}
      <div className="min-h-0 flex-1 overflow-y-auto p-2 scrollbar-thin">{renderList()}</div>
    </WorkspacePanel>
  );
};

export default SourcesPanel;
